/**
 * Per-card deck-construction rules, shared by `validateDeck` and the add-guards so the two can never
 * disagree.
 */
import type { FormatRules } from './formats';
import { cardNameKey, displayCardName, foldText, rulesName } from './names';
import { isUniqueText, unlimitedCopiesName } from './text';
import type { DeckRulesCard } from './types';

/** Domain every deck may use besides its legend's (CR 103.2.b.4). */
export const COLORLESS = 'Colorless';

const MAIN_DECK_TYPES: ReadonlySet<string> = new Set(['Unit', 'Spell', 'Gear']);

/** The name shown in messages: variant suffixes and parentheticals removed. */
export function cardLabel(card: Pick<DeckRulesCard, 'name'>): string {
  return displayCardName(rulesName(card.name));
}

/**
 * The legend's champion tag (CR 133.8.b): the tag equal to the champion's name before the comma
 * ("Kennen, Heart of the Tempest" with tags [Yordle, Kennen] → "Kennen"), else its last tag, else the
 * name before the comma. A shared region or race tag (Yordle) never makes another champion eligible.
 */
export function championTag(legend: Pick<DeckRulesCard, 'name' | 'tags'>): string | null {
  const match = /^([^,]+),/.exec(cardLabel(legend));
  const champion = match?.[1]?.trim() ?? null;
  if (champion) {
    const key = foldText(champion);
    const own = legend.tags.find((t) => foldText(t.trim()) === key);
    if (own) return own;
  }
  return legend.tags[legend.tags.length - 1] ?? champion;
}

/** Whether the card carries `tag` (accents and case folded). */
export function hasTag(card: Pick<DeckRulesCard, 'tags'>, tag: string): boolean {
  const key = foldText(tag.trim());
  return card.tags.some((t) => foldText(t.trim()) === key);
}

/** The legend's domain identity (CR 103.2.b.2), without Colorless. */
export function legendDomains(legend: Pick<DeckRulesCard, 'domains'>): string[] {
  return legend.domains.filter((d) => d !== COLORLESS);
}

/** Every domain of the card is in the identity; Colorless fits any deck (CR 103.2.b.3-4). */
export function fitsIdentity(card: Pick<DeckRulesCard, 'domains'>, identity: readonly string[]): boolean {
  return card.domains.every((d) => d === COLORLESS || identity.includes(d));
}

/** A Legend card (CR 103.2). */
export function isLegend(card: Pick<DeckRulesCard, 'type'>): boolean {
  return card.type === 'Legend';
}

/** A champion unit, the only cards that can be the Chosen Champion (CR 103.3.a). */
export function isChampionUnit(card: Pick<DeckRulesCard, 'type' | 'supertype'>): boolean {
  return card.type === 'Unit' && card.supertype === 'Champion';
}

/** Units, spells and gear (no tokens): what the Main Deck and the sideboard may hold (CR 103.3, TR 601.1.c.2). */
export function isMainDeckCard(card: Pick<DeckRulesCard, 'type' | 'supertype'>): boolean {
  return MAIN_DECK_TYPES.has(card.type) && card.supertype !== 'Token';
}

/** A Signature card (CR 103.3.d). */
export function isSignature(card: Pick<DeckRulesCard, 'supertype'>): boolean {
  return card.supertype === 'Signature';
}

/** `[Unique]`: at most one copy of its name (CR 825.3.a). */
export function isUnique(card: Pick<DeckRulesCard, 'text' | 'keywords'>): boolean {
  return isUniqueText(card.text, card.keywords);
}

/** Card text lifts the copy limit for its own name (CR 002), e.g. Spiderling. */
export function liftsCopyLimit(card: Pick<DeckRulesCard, 'name' | 'text'>): boolean {
  const named = unlimitedCopiesName(card.text);
  return named !== null && cardNameKey(named) === cardNameKey(card.name);
}

/** Copies of this card's name a deck may hold across champion, Main Deck and sideboard. */
export function copyLimitFor(card: Pick<DeckRulesCard, 'name' | 'text' | 'keywords'>, format: FormatRules): number {
  if (isUnique(card)) return 1;
  return liftsCopyLimit(card) ? Infinity : format.copyLimit;
}

/** Some printing of the card's name is in one of the format's legal sets (TR 601.2.a, 601.3). */
export function isLegalInFormat(card: Pick<DeckRulesCard, 'sets'>, format: FormatRules): boolean {
  return card.sets.some((s) => format.legalSets.includes(s));
}

/** Champion units that can be this legend's Chosen Champion: they carry its champion tag (CR 103.3.a.2). */
export function championsForLegend<T extends Pick<DeckRulesCard, 'type' | 'supertype' | 'tags'>>(
  legend: Pick<DeckRulesCard, 'name' | 'tags'>,
  cards: readonly T[],
): T[] {
  const tag = championTag(legend);
  if (!tag) return [];
  return cards.filter((c) => isChampionUnit(c) && hasTag(c, tag));
}
